import styled from '@emotion/styled';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import ContentLightBox from '@ep/components/ContentLightBox';
import SeeMoreLink from '@ep/components/Link';
import Media from '@ep/components/Media';
import ModalDialog from '@ep/components/ModalDialog';
import { H4 } from '@ep/components/Typography';
import { Body, H1 } from '@ep/templates/ProductPage';
import theme from '@ep/theme';
import getHtmlProp from '@ep/utils/getHtmlProp';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import React, { useEffect } from 'react';
import Fade from 'react-reveal';
import {
  CheckProps,
  ContainerProps,
  ExpandingTextBoxProps,
  MediaModuleProps,
  StyledMediaProps,
  TextBoxProps,
  TextsProps,
} from './MediaModuleProps';

const StyledContainer = styled(Container)<ContainerProps>`
  a:link,
  a:hover,
  a:visited {
    color: ${theme.colors.epLinkBlue};
  }
  a:hover {
    text-decoration: underline;
  }
  display: flex;
  align-items: space-between;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0rem;
  flex-direction: ${({ right }) => (right ? 'row-reverse' : 'row')};
  padding: ${({ noPaddingTop }) => (noPaddingTop ? '3.75rem' : '4.75rem')} 0
    3.75rem;

  ul {
    padding: 0;
    margin: 0;
    li {
      list-style: none;
      padding-left: 2rem;
      position: relative;
      &:before {
        content: '>';
        position: absolute;
        left: 0;
        top: 0;
        color: ${theme.colors.oceanBlue};
      }
    }
  }

  ${theme.mediaQueries.tablet} {
    padding: ${({ noPaddingTop }) => (noPaddingTop ? '1rem' : '2rem')} 0 2rem;
    ${({ bottom }) =>
      `
          flex-direction: ${bottom ? 'column-reverse' : 'column'};
          align-items: stretch;
          margin: 0 auto;
          max-width: 28rem;
        `}
    h2 {
      white-space: wrap;
      font-size: 1.8em;
      text-align: center;
    }
  }

  ${theme.mediaQueries.mobile} {
    flex-direction: ${({ bottom }) => (bottom ? 'column-reverse' : 'column')};
    align-items: stretch;
    padding: ${({ noPaddingTop }) => (noPaddingTop ? '1.75rem' : '2.5rem')} 0
      1.75rem;
    h2 {
      font-size: 1.6em;
      text-align: center;
    }
  }
`;

const StyledMedia = styled(Media)<StyledMediaProps>`
  ${theme.mediaQueries.mobileAndTablet} {
    ${({ bottom }) => (bottom ? 'margin-top: 2rem;' : 'margin-bottom: 2rem;')};
  }
`;

const MediaContainer = styled.div<StyledMediaProps>`
  width: 50%;
  ${theme.mediaQueries.mobileAndTablet} {
    ${({ bottom }) => (bottom ? 'margin-top: 2rem;' : 'margin-bottom: 2rem;')};
    width: 100%;
  }
`;

const Texts = styled.div<TextsProps>`
  font-family: ${theme.typography.lightFont};
  font-weight: 500;
  font-size: 1.2rem;
  width: ${({ expand }) => (expand ? '100%' : '50%')};
  display: flex;
  align-items: center;
  transition-duration: 1s;
`;

const TextBox = styled.div<TextBoxProps>`
  width: 50%;

  ${theme.mediaQueries.desktop} {
    ${({ right }) => (right ? `padding-right: 4rem` : `padding-left: 4rem`)};
  }
  ${theme.mediaQueries.mobileAndTablet} {
    ${({ right }) => (right ? `padding-right: 2rem` : `padding-left: 2rem`)};
    width: 100%;
  }
`;

const SuperHeading = styled(H4)`
  margin-bottom: 0;
  font-color: ${theme.colors.darkBlue};

  @media (max-width: ${theme.breakpoints.optimalMobile + 200}px) {
    text-align: center;
  }
`;

const LearnMore = styled(Button)`
  font-family: ${theme.typography.primaryFont.regular};
  font-size: 1.5rem;
  margin-top: 2rem;
  height: 3.75rem;
  padding: 0 1.5rem;
`;

const StyledSeeMoreLink = styled(SeeMoreLink)`
  font-family: ${theme.typography.primaryFont.regular};
  border: 1px solid;
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  font-size: 1.5rem;
  margin-top: 2rem;
  height: 3.75rem;
  padding: 0 1.5rem;
  border-color: ${theme.colors.darkGray} !important;
  color: ${theme.colors.darkGray} !important;
  border-radius: 0.75rem;
  &:hover {
    background-color: ${theme.colors.mediumGreen};
    border-color: ${theme.colors.mediumGreen} !important;
    color: ${theme.colors.white} !important;
    text-decoration: none !important;
  }
`;

const MediaHolder = styled.div`
  position: relative;
  ${theme.mediaQueries.tablet} { 
    display: flex;
    justify-content: center;
  }
  ${theme.mediaQueries.mobile} {
    display: flex;
    justify-content: center;
  }
`;

const CheckBoxes = styled.div`
  position: absolute;
  z-index: 1;
  top: 2rem;
  right: 0;
  width: 14rem;
`;

const CheckContainer = styled.div`
  background-color: ${theme.colors.white};
  box-shadow: 4px 4px 8px ${theme.colors.darkGray};
  padding: 0.25rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
`;

const CheckCircle = styled.div`
  border-radius: 0.5rem;
  height: 1rem;
  width: 1rem;
  background-color: ${theme.colors.mantisGreen};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CoverContainer = styled.div`
  width: 0.7rem;
  height: 0.7rem;
  margin-top: -0.8rem;
  display: flex;
  justify-content: flex-end;
`;
const Cover = styled.div<CheckProps>`
  background-color: ${theme.colors.mantisGreen};
  width: 0.7rem;
  height: 0.7rem;
  animation-name: wipeCheck;
  animation-duration: 0.25s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  animation-play-state: ${({ run }) => (run ? 'running' : 'pause')};
  animation-delay: ${({ delay }) => `${delay}s`};

  @keyframes wipeCheck {
    from {
      width: 0.7rem;
    }
    to {
      width: 0rem;
    }
  }
`;

const StyledCheckIcon = styled(CheckIcon)`
  color: ${theme.colors.white};
  font-size: 0.8rem;
`;

const ShownTextBox = styled.div``;

const HiddenTextBox = styled.div<ExpandingTextBoxProps>`
  margin: ${({ expand }) => (expand ? '1rem 0' : ' 0.5rem 0')};
  overflow-y: hidden;
  height: ${({ expand, height }) => (expand ? height + 'px' : '0')};
  transition: height 0.5s ease;
`;

const ExpandLink = styled.a`
  text-decoration: none;
  color: ${theme.colors.epLinkBlue};
  cursor: pointer;
`;

const Header = styled.h3`
  color: ${theme.colors.duskBlue};
  font-family: ${theme.typography.semiBoldFont};
  font-size: 2.625rem;
  line-height: 1;
`;

function MediaModule({
  themeColor,
  superHeading,
  heading,
  bodyContent,
  bodyClassName,
  buttonLabel,
  buttonLink,
  media,
  mediaCaption,
  aspectRatio,
  big,
  small,
  right,
  bottom,
  gray,
  noPaddingTop,
  lightboxImage,
  textExpand,
  expandLinkText,
  collapseLinkText,
  lightboxText,
  imageCheckboxes,
  scrollToLink,
}: MediaModuleProps) {
  const [open, setOpen] = React.useState(false);
  const [openLightBox, setOpenLightBox] = React.useState(false);
  const [expandText, setExpandText] = React.useState(false);
  const [animation, setAnimation] = React.useState(false);
  const [textHeight, setTextHeight] = React.useState(0);

  const textRef = React.useRef(null);

  useEffect(() => {
    if (lightboxText) {
      setTextHeight(textRef.current.clientHeight);
    }
  }, [lightboxText]);

  // check wether the media is clickable or not
  const isClickable = () =>
    media.options && media.options.search('clickable') ? 'pointer' : 'default';

  const onMediaClick = () => {
    if (media.options && media.options.search('clickable')) setOpen(true);
  };

  const handleButtonClick = (moduleName, buttonLabel) => {
    // dataLayer.push({
    //   event:
    //     'Page Interaction – user clicked on media module button - ' +
    //     moduleName +
    //     ' - ' +
    //     buttonLabel,
    // });

    if (textExpand) {
      setExpandText(!expandText);
    } else {
      setOpenLightBox(true);
    }
  };

  return (
    <StyledContainer
      backgroundColor={
        gray || themeColor === 'page_break' ? theme.colors.lightGray : undefined
      }
      big={big}
      small={small}
      right={right}
      bottom={bottom}
      noPaddingTop={noPaddingTop}
      id={scrollToLink}
    >
      <MediaContainer
        right={right}
        bottom={bottom}
        big={big}
        small={small}
        expand={expandText}
      >
        <Fade bottom cascade onReveal={() => setAnimation(true)}>
          <MediaHolder>
            <StyledMedia
              media={media}
              aspectRatio={aspectRatio}
              caption={mediaCaption}
              big={big}
              small={small}
              fill={big}
              right={right}
              bottom={bottom}
              expand={expandText}
              cover
              lazy
              onClick={() => onMediaClick()}
              style={{ cursor: `${isClickable}` }}
              lightbox
            />
            <CheckBoxes>
              {imageCheckboxes?.map((check, index) => (
                <CheckContainer key={`new_media_animation_checkbox__${index}`}>
                  <CheckCircle>
                    <StyledCheckIcon />
                    <CoverContainer>
                      <Cover delay={index + 0.5} run={animation} />
                    </CoverContainer>
                  </CheckCircle>
                  <Body style={{ fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                    {check}
                  </Body>
                </CheckContainer>
              ))}
            </CheckBoxes>
          </MediaHolder>
        </Fade>
      </MediaContainer>
      <TextBox right={right}>
        <ShownTextBox>
          {/* Media Super Heading */}
          {superHeading && <SuperHeading>{superHeading}</SuperHeading>}
          {/* Media Heading */}
          {heading && <Header>{heading}</Header>}
          {/* Body Content */}
          {bodyContent && (
            <Body
              className={bodyClassName}
              {...getHtmlProp(bodyContent.html)}
            />
          )}
          {lightboxText && (
            <HiddenTextBox expand={expandText} height={textHeight}>
              <Body
                ref={textRef}
                className={bodyClassName}
                {...getHtmlProp(lightboxText.html)}
              />
            </HiddenTextBox>
          )}
        </ShownTextBox>
        {/* Action Buttons */}
        {buttonLabel && buttonLink && !lightboxText && (
          <StyledSeeMoreLink to={buttonLink}>{buttonLabel}</StyledSeeMoreLink>
        )}
        {/* Learn More Button if Lightbox contetn is exist */}
        {textExpand && lightboxText && (
          <ExpandLink
            onClick={() => {
              handleButtonClick(heading, buttonLabel);
            }}
          >
            {expandText
              ? collapseLinkText
                ? collapseLinkText
                : 'collapse'
              : expandLinkText
              ? expandLinkText
              : 'expand'}
          </ExpandLink>
        )}
        {!textExpand && buttonLabel && lightboxText && (
          <LearnMore
            outlined
            color="black"
            label={buttonLabel}
            onClick={() => {
              handleButtonClick(heading, buttonLabel);
            }}
          />
        )}
      </TextBox>
      <ModalDialog open={open} setOpen={setOpen} modalDialogMedia={media} />
      {openLightBox && (
        <ContentLightBox
          openLightBox={openLightBox}
          setOpenLightBox={setOpenLightBox}
          contentLightBoxText={lightboxText}
          contentLightBoxImg={lightboxImage}
        />
      )}
    </StyledContainer>
  );
}

export default React.memo(MediaModule);
